"use client";

import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Checkbox,
  Radio,
  Flex,
  Button,
  Alert,
  App,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { usercontext } from "@/lib/context";
import { setPhoneInputInitialValue } from "../CustomPhoneInput";
import CustomPhoneInput from "../CustomPhoneInput";
import {
  formatFullPhone,
  REGISTRATION_MESSAGES,
} from "@/lib/registrationHelpers";
import { scriptQueryValidation } from "@/lib/validations";

const renderField = (field) => {
  const key = field.id;
  const name = field.name;
  const required = field.is_required;

  let options = [];
  try {
    options = field.options ? JSON.parse(field.options) : [];
  } catch (e) {
    console.error("Invalid options for field:", field.name);
    options = [];
  }

  switch (field.type) {
    case "short_answer": {
      const lowerName = name ? name.toLowerCase().trim() : "";
      const isFirstName =
        lowerName === "first name" || lowerName === "firstname";
      const isLastName = lowerName === "last name" || lowerName === "lastname";
      const isFirstOrLastName = isFirstName || isLastName;
      const maxLen = isFirstOrLastName ? 20 : 200;

      const fieldRules = [
        { required, message: `${name} is required` },
        ...(isFirstName
          ? [
              { min: 2, message: "First name must be at least 2 characters" },
              { max: 20, message: "First name cannot exceed 20 characters" },
            ]
          : isLastName
            ? [
                { min: 2, message: "Last name must be at least 2 characters" },
                { max: 20, message: "Last name cannot exceed 20 characters" },
              ]
            : [{ max: 200, message: `${name} cannot exceed 200 characters` }]),
        ...scriptQueryValidation(name),
      ];

      return (
        <Form.Item
          key={key}
          name={name.replaceAll(" ", "_").toLowerCase()}
          label={name}
          rules={fieldRules}
        >
          <Input placeholder={name} maxLength={maxLen} />
        </Form.Item>
      );
    }

    case "checkbox": {
      const labels = options.map((o) => (typeof o === "object" ? o.label : o));
      const hasDuplicates = new Set(labels).size < labels.length;

      return (
        <Form.Item
          key={key}
          name={name.replaceAll(" ", "_").toLowerCase()}
          label={name}
          rules={[{ required, message: `${name} is required` }]}
        >
          <Checkbox.Group>
            {options.map((o, idx) => {
              const labelText = typeof o === "object" ? o.label : o;
              const val = hasDuplicates ? `${labelText}___${idx}` : labelText;
              return (
                <Checkbox key={idx} value={val}>
                  {labelText}
                </Checkbox>
              );
            })}
          </Checkbox.Group>
        </Form.Item>
      );
    }

    case "multiple_choice": {
      const labels = options.map((o) => (typeof o === "object" ? o.label : o));
      const hasDuplicates = new Set(labels).size < labels.length;

      return (
        <Form.Item
          key={key}
          name={name.replaceAll(" ", "_").toLowerCase()}
          label={name}
          rules={[{ required, message: `${name} is required` }]}
        >
          <Radio.Group>
            {options.map((o, idx) => {
              const labelText = typeof o === "object" ? o.label : o;
              const val = hasDuplicates ? `${labelText}___${idx}` : labelText;
              return (
                <Radio key={idx} value={val}>
                  {labelText}
                </Radio>
              );
            })}
          </Radio.Group>
        </Form.Item>
      );
    }

    default:
      return null;
  }
};

const CreateCustomForm = ({
  ticket_custom_field,
  show_single_registration,
  is_unique_registration,
  setCustomModal,
  qty,
  loading,
}) => {
  const { message } = App.useApp();
  const [step, setStep] = useState(0);
  const [form] = useForm();
  const { user } = useContext(usercontext);
  const usedPhonesRef = useRef(new Set());

  useEffect(() => {
    form.setFieldsValue({ ...user, ...setPhoneInputInitialValue(user?.phone) });
  }, []);

  const registerStepPhone = (values) => {
    const fullPhone = formatFullPhone(values?.country_code, values?.phone);
    if (fullPhone) {
      usedPhonesRef.current.add(fullPhone);
    }
  };

  return (
    <>
      {is_unique_registration && (
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="Unique mobile number required"
          description={
            show_single_registration
              ? REGISTRATION_MESSAGES.UNIQUE_MOBILE_INFO
              : REGISTRATION_MESSAGES.UNIQUE_MOBILE_PER_TICKET
          }
        />
      )}

      {Array(show_single_registration ? 1 : qty)
        .fill(0)
        .map((_, index) => (
          <Form
            layout="vertical"
            key={index}
            form={index === 0 ? form : null}
            onFinish={(values) => {
              if (is_unique_registration && !show_single_registration) {
                const fullPhone = formatFullPhone(
                  values?.country_code,
                  values?.phone,
                );
                if (fullPhone && usedPhonesRef.current.has(fullPhone)) {
                  message.error(REGISTRATION_MESSAGES.DUPLICATE_IN_FORM);
                  return;
                }
                registerStepPhone(values);
              }

              if (!show_single_registration && qty - 1 !== step) {
                setStep((prev) => prev + 1);
              }
              // On the last step, leave the modal open — Booking.js closes
              // it only once createOrder actually succeeds. Otherwise a
              // failed order (sold out, network error, validation) would
              // close the modal while the user still had no idea it failed,
              // stranding them with just a toast and lost form state.
            }}
            name={`${index}`}
            className={`${
              index === step ? "block" : "hidden"
            } ${index} ${step}`}
          >
            <>
              <span className="text-[16px]">Phone Number</span>
              <CustomPhoneInput />
            </>
            {ticket_custom_field
              .sort((a, b) => a.sort_order - b.sort_order)
              .map(renderField)}

            <Flex gap={10} className="w-full" justify="space-between">
              {index !== 0 && (
                <Button
                  type="default"
                  onClick={() => {
                    setStep((prev) => prev - 1);
                  }}
                >
                  Back
                </Button>
              )}

              <Flex gap={20} className="w-full" justify="flex-end">
                <Button
                  type="default"
                  disabled={loading}
                  onClick={() => {
                    setCustomModal(false);
                    setStep(0);
                    usedPhonesRef.current = new Set();
                  }}
                >
                  Cancel
                </Button>

                <Button
                  type="primary"
                  htmlType="submit"
                  loading={
                    (!show_single_registration ? index === qty - 1 : true) &&
                    loading
                  }
                >
                  {!show_single_registration
                    ? index !== qty - 1
                      ? "Next"
                      : "Submit"
                    : "Submit"}
                </Button>
              </Flex>
            </Flex>
          </Form>
        ))}
    </>
  );
};

export default CreateCustomForm;
